import { ChartTextProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property';
import { MarkerType } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import sortBy from 'lodash/sortBy';
import { MarkerProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/marker';
import type { Nullable } from '../chart-shape-property.interface';
import type { ILegendItem, ILegendProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type { IChartLegend, IChartManualLayout } from '../../../Interface/chart-v3/chart-properties.interface';
import {
    STLayoutMode, STLegendPos,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';

export class LegendProperty extends ChartTextProperty<IChartLegend> implements ILegendProperty {
    public items: ILegendItem[];

    public init() {
        this.items = this.getLegendItems();
        return this;
    }

    public updateProps(props: Partial<IChartLegend>, removeProps?: Partial<Nullable<IChartLegend>>): void {
        this.model.updateAndRemoveProps({ legend: props }, { legend: removeProps });
    }

    public removeLegend(): void {
        this.model.removeProps({ legend: null });
    }

    /**
     * 更新图例自定义布局
     */
    public updateLayout(layout: IChartManualLayout) {
        const legendProps: IChartLegend = {
            layout,
        };

        if (layout.top !== undefined || layout.left !== undefined) {
            // 补充修改layout自定义位置的必要字段，否则office不生效
            if (!this.uiData.layout?.leftMode) {
                layout.leftMode = STLayoutMode.edge;
            }
            if (!this.uiData.layout?.topMode) {
                layout.topMode = STLayoutMode.edge;
            }
            if (!this.uiData.position) {
                legendProps.position = STLegendPos.b;
            }
        }

        this.model.updateProps({ legend: legendProps });
    }

    /* 图例需要在子表全部初始化完毕后进行初始化，因为依赖所有的子表数据，对图例内容做聚合 */
    private getLegendItems(): ILegendItem[] {
        const subCharts = this.context.getPropertyModule('subCharts');

        const legendItems: ILegendItem[] = [];
        subCharts?.forEach((subChartProperty) => {
            if (!subChartProperty) return;
            legendItems.push(...this.getSubChartLegendItems(subChartProperty));
        });

        // 进行最终的排序
        return this.sortLegend(legendItems);
    }

    private getSubChartLegendItems(subChart: IBaseSubChartProperty): ILegendItem[] {
        const legendContentType = subChart.getLegendContentType();
        const { series } = subChart;

        let legendItems: ILegendItem[] = legendContentType === 'series'
            ? series.filter(ser => !ser.disable).map(ser => ({
                name: ser.name,
                paragraphList: this.uiData.paragraphList,
                marker: subChart.getLegendMarker(
                    ser.props,
                    ser.marker && new MarkerProperty(ser.marker, this.context).init(this),
                ),
                ser,
            })) : subChart.data.map(dataItem => ({
                name: dataItem.category as string,
                paragraphList: this.uiData.paragraphList,
                marker: subChart.getLegendMarker(
                    dataItem.props || { style: {} },
                    dataItem.marker,
                ),
                ser: dataItem.ser,
                dataItem,
            }));

        legendItems = this.handleLegendItemsWithLegendEntry(legendItems);
        legendItems = this.handleLegendItemsWithTrendLine(legendItems, subChart);
        legendItems = subChart.processLegendItems(legendItems);

        return legendItems;
    }

    private handleLegendItemsWithLegendEntry(legendItems: ILegendItem[]): ILegendItem[] {
        const { legendEntryList } = this.uiData;
        if (legendEntryList) {
            return legendItems
                .filter((item, itemIndex) => !legendEntryList?.find(entry => entry.delete && entry.id === itemIndex))
                .map((item, index) => {
                    const legendEntry = legendEntryList?.find(entry => entry.id === index);
                    if (legendEntry) {
                        return {
                            ...item,
                            paragraphList: legendEntry.paragraphList,
                        };
                    }

                    return item;
                })
            ;
        }

        return legendItems;
    }

    private handleLegendItemsWithTrendLine(legendItems: ILegendItem[], subChart: IBaseSubChartProperty): ILegendItem[] {
        const { trendlines } = subChart;
        // 每条趋势线都有一个图例项
        const trendLineLegendItems: ILegendItem[] = trendlines?.filter(trendline => trendline.isSupport
            && trendline.name)
            .map(trendline => ({
                name: trendline.name || '',
                type: 'trendline',
                marker: {
                    symbol: MarkerType.Line,
                    props: trendline.props || { style: {} },
                },
                ser: trendline.series,
            })) || [];

        return [...legendItems, ...trendLineLegendItems];
    }

    // 将趋势线图例统一放到最后
    private sortLegend(legendItems: ILegendItem[]) {
        return sortBy(legendItems, item => (item.type ? 1 : -1));
    }

    private get model() {
        return this.context.getChartModel();
    }
}
